import type {BlockRender} from 'quickstart/types'
import {createRoot, hydrateRoot} from 'react-dom/client'
import {BrowserRouter} from 'react-router-dom'
import {logger} from 'tizra'
import {BlockWrapper} from './BlockWrapper'
import {makeIdentifierPrefix} from './utils.tsx'

const log = logger('blocks/render')

/**
 * Client-side block renderer.
 */
export const renderBlock: BlockRender = ({
  blockId,
  config,
  context,
  hydrate,
  info: {displayName, LayoutBlock},
  queryClient,
  sortKey,
  store,
  // BlockRenderProps
  root: container,
  // BlockRenderPropsTest
  render,
  Router = BrowserRouter,
  routerProps,
}) => {
  const blog = log.logger(displayName)

  // Make sure we have a block id, since we use this for unique ids in
  // multi-root React.
  blockId = blog.assert(blockId, 'missing blockId') || 'ZZZ'

  const wrappedBlock = (
    <BlockWrapper
      context={context}
      hydrate={hydrate}
      queryClient={queryClient}
      Router={Router}
      routerProps={routerProps}
      store={store}
    >
      <LayoutBlock blockId={blockId} config={config} sortKey={sortKey} />
    </BlockWrapper>
  )

  // Under test, pass back whatever the render function returns.
  if (render) {
    return render(wrappedBlock)
  }

  // In normal operation, render and return the cleanup function.
  const identifierPrefix = makeIdentifierPrefix(blockId)
  let root: ReturnType<typeof createRoot>
  if (hydrate) {
    root = hydrateRoot(container!, wrappedBlock, {identifierPrefix})
  } else {
    root = createRoot(container!, {identifierPrefix})
    root.render(wrappedBlock)
  }
  return () => {
    blog.log('unmounting')
    root.unmount()
  }
}
